import React from 'react';
import ConnectionPersistenceStatus from './ConnectionPersistenceStatus';
import ReminderPreferences from './ReminderPreferences';
import { useSettingsState } from './settings/useSettingsState';
import { useSettingsLoaders } from './settings/useSettingsLoaders';
import { useSettingsActions } from './settings/useSettingsActions';
import SettingsLayout from './settings/SettingsLayout';
import SettingsTabs from './settings/SettingsTabs';
import GeneralSettingsTab from './settings/tabs/GeneralSettingsTab';
import FirebaseSettingsTab from './settings/tabs/FirebaseSettingsTab';
import EmailSettingsTab from './settings/tabs/EmailSettingsTab';
import OpenAISettingsTab from './settings/tabs/OpenAISettingsTab';
import ResendSettingsTab from './settings/tabs/ResendSettingsTab';
import StorageSettingsTab from './settings/tabs/StorageSettingsTab';
import MonitoringSettingsTab from './settings/tabs/MonitoringSettingsTab';
import BudgetSettingsTab from './settings/tabs/BudgetSettingsTab';
import type { SettingsTab } from './settings/types';

const Settings: React.FC = () => {
  const state = useSettingsState();
  const loaders = useSettingsLoaders(state);
  const actions = useSettingsActions(state, loaders);

  const { configs, setters, ui } = state;

  const renderTab = (tab: SettingsTab) => {
    switch (tab) {
      case 'general':
        return (
          <GeneralSettingsTab
            config={configs.generalConfig}
            onChange={setters.setGeneralConfig}
          />
        );
      case 'connections':
        return <ConnectionPersistenceStatus />;
      case 'reminders':
        return <ReminderPreferences />;
      case 'firebase':
        return (
          <FirebaseSettingsTab
            config={configs.firebaseConfig}
            onChange={setters.setFirebaseConfig}
            testResults={ui.testResults}
            onTest={actions.testConnection}
          />
        );
      case 'email':
        return (
          <EmailSettingsTab
            config={configs.emailConfig}
            onChange={setters.setEmailConfig}
            testResults={ui.testResults}
            onTest={actions.testConnection}
            showPasswords={ui.showPasswords}
            onTogglePassword={actions.togglePasswordVisibility}
          />
        );
      case 'openai':
        return (
          <OpenAISettingsTab
            config={configs.openaiConfig}
            onChange={setters.setOpenaiConfig}
            testResults={ui.testResults}
            onTest={actions.testConnection}
            onRefreshBilling={loaders.loadOpenAIBillingInfo}
            showPasswords={ui.showPasswords}
            onTogglePassword={actions.togglePasswordVisibility}
          />
        );
      case 'resend':
        return (
          <ResendSettingsTab
            config={configs.resendConfig}
            onChange={setters.setResendConfig}
            testResults={ui.testResults}
            onTest={actions.testConnection}
            showPasswords={ui.showPasswords}
            onTogglePassword={actions.togglePasswordVisibility}
          />
        );
      case 'storage':
        return (
          <StorageSettingsTab
            config={configs.storageConfig}
            onChange={setters.setStorageConfig}
            testResults={ui.testResults}
            onTest={actions.handleTestStorageConnection}
            onReload={actions.handleReloadStorageFromDB}
            showPasswords={ui.showPasswords}
            onTogglePassword={actions.togglePasswordVisibility}
          />
        );
      case 'monitoring':
        return (
          <MonitoringSettingsTab
            config={configs.grafanaConfig}
            onChange={setters.setGrafanaConfig}
            onTest={actions.handleTestGrafanaConnection}
            testingConnection={ui.testingGrafanaConnection}
            testResult={ui.grafanaTestResult}
            showPasswords={ui.showPasswords}
            onTogglePassword={actions.togglePasswordVisibility}
          />
        );
      case 'budget':
        return (
          <BudgetSettingsTab
            config={configs.budgetConfig}
            onChange={setters.setBudgetConfig}
            onTest={actions.testBudgetConnection}
            onSave={actions.saveBudgetConfig}
            testingConnection={ui.testingBudgetConnection}
            savingConfig={ui.savingBudgetConfig}
            testResult={ui.budgetTestResult}
            showApiKey={ui.showBudgetApiKey}
            onToggleApiKey={() => ui.setShowBudgetApiKey(!ui.showBudgetApiKey)}
          />
        );
      default:
        return (
          <GeneralSettingsTab
            config={configs.generalConfig}
            onChange={setters.setGeneralConfig}
          />
        );
    }
  };

  return (
    <SettingsLayout onSave={actions.saveConfiguration} isSaving={ui.isSaving}>
      <SettingsTabs activeTab={ui.activeTab} onTabChange={ui.setActiveTab} />
      <div className="p-6">
        {renderTab(ui.activeTab)}
      </div>
    </SettingsLayout>
  );
};

export default Settings;
